import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format currency
export function formatCurrency(amount, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

// Format large numbers
export function formatNumber(num) {
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1) + 'B';
  }
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num?.toString() || '0';
}

// Format date
export function formatDate(date, options = {}) {
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };
  return new Date(date).toLocaleDateString('en-US', defaultOptions);
}

// Format datetime
export function formatDateTime(date) {
  return new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Format relative time
export function formatRelativeTime(date) {
  const now = new Date();
  const then = new Date(date);
  const diff = now - then;
  
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return formatDate(date);
}

// Get risk level color
export function getRiskColor(level) {
  const colors = {
    critical: '#FF003D',
    high: '#FF6B00',
    medium: '#FF9900',
    low: '#00FF94',
  };
  return colors[level?.toLowerCase()] || colors.low;
}

// Get risk level class
export function getRiskClass(level) {
  const classes = {
    critical: 'bg-risk-critical text-[#FF003D] border-[#FF003D]',
    high: 'bg-risk-high text-[#FF6B00] border-[#FF6B00]',
    medium: 'bg-risk-medium text-[#FF9900] border-[#FF9900]',
    low: 'bg-risk-low text-[#00FF94] border-[#00FF94]',
  };
  return classes[level?.toLowerCase()] || classes.low;
}

// Get status color
export function getStatusColor(status) {
  const colors = {
    new: '#00F0FF',
    investigating: '#FF9900',
    escalated: '#FF003D',
    resolved: '#00FF94',
    false_positive: '#64748B',
    open: '#00F0FF',
    in_progress: '#FF9900',
    pending_review: '#A855F7',
    closed: '#00FF94',
    archived: '#64748B',
  };
  return colors[status?.toLowerCase()] || '#64748B';
}

// Truncate text
export function truncate(str, length = 50) {
  if (!str) return '';
  return str.length > length ? str.substring(0, length) + '...' : str;
}

// Truncate wallet address
export function truncateAddress(address, start = 6, end = 4) {
  if (!address) return '';
  if (address.length <= start + end) return address;
  return `${address.slice(0, start)}...${address.slice(-end)}`;
}

// Generate random ID
export function generateId() {
  return Math.random().toString(36).substring(2, 15);
}

// Debounce function
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Parse error message
export function parseError(error) {
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
}
